/**
 * The falling sum block: instruction line + typeset expression, sitting at
 * its (quantized) row. Each gravity step eases over ~90 ms. The expression
 * auto-shrinks to fit the block (never below 16 px); a stroke creeping round
 * the border shows the time running out; in the lock delay the block wobbles
 * gently on the stack.
 */
import { useMemo, useRef, type CSSProperties } from 'react';
import type { Locale, Problem } from '@dropstack/core';
import { useElementSize, useFitFont } from './hooks.ts';
import { InstructionLine, MathText } from './MathText.tsx';
import type { MathView } from './mathView.ts';
import { blockOffsetPx } from './playfield.ts';
import { TIMING } from './timeline.ts';

export type SumBlockMode =
  /** Falling under gravity (quantized steps, eased). */
  | 'falling'
  /** Resting on the stack in the lock delay. */
  | 'resting'
  /** Answered correctly: dropping fast, blank filled with the answer. */
  | 'correct'
  /** Answered wrongly / timed out: dropping (or locking) before it turns into bricks. */
  | 'wrong';

export interface SumBlockProps {
  problem: Problem;
  locale: Locale;
  mode: SumBlockMode;
  /** Row (0 = top), float. */
  row: number;
  landingRow: number;
  /** One well row (px) and the well's inner width (px). */
  rowPx: number;
  wellWidth: number;
  /** 1 → 0 while falling; null hides the timer. */
  timeLeft: number | null;
  /** Draw the blank filled with this (the answer, after a correct answer). Keep it referentially stable. */
  fill: MathView[] | null;
  /** "Larger text" setting: scales the preferred maths size. */
  textScale: number;
  reducedMotion: boolean;
}

/** Preferred maths size for a well this wide (px), before any shrinking. */
export function baseMathPx(wellWidth: number, textScale: number): number {
  return Math.round(Math.min(46, Math.max(22, wellWidth / 10.5)) * textScale);
}

/** The instruction line's size (px): readable, clearly secondary to the maths. */
export function instructionPx(basePx: number): number {
  return Math.round(Math.min(18, Math.max(13, basePx * 0.5)));
}

/** Vertical room taken by the block's padding and border (px; matches game.css). */
const BLOCK_CHROME_PX = 22;

export function SumBlock(props: SumBlockProps) {
  const { problem, locale, mode, row, landingRow, rowPx, wellWidth, timeLeft, fill, textScale, reducedMotion } = props;
  const blockRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const mathRef = useRef<HTMLSpanElement>(null);

  const block = useElementSize(blockRef, 'border');
  const content = useElementSize(contentRef);
  const basePx = baseMathPx(wellWidth, textScale);
  const instrPx = instructionPx(basePx);
  const hasInstruction = !!problem.instruction;
  // Room above the stack for this block, less its chrome and the instruction line.
  const freeHeight = Math.max(rowPx, (landingRow + 1) * rowPx);
  const mathPx = useFitFont({
    contentRef: mathRef,
    availableWidth: content.width,
    availableHeight: freeHeight - BLOCK_CHROME_PX - (hasInstruction ? instrPx * 1.35 : 0),
    basePx,
    contentKey: `${problem.id}:${fill ? 'filled' : 'blank'}`,
  });

  const mathStyle = useMemo(() => ({ fontSize: mathPx }), [mathPx]);
  const instructionStyle = useMemo(() => ({ fontSize: instrPx }), [instrPx]);

  const top = blockOffsetPx({ row, landingRow, rowPx, blockPx: Math.max(rowPx, block.height) });
  const style: CSSProperties = {
    minHeight: rowPx,
    transform: `translate3d(0, ${top.toFixed(2)}px, 0)`,
    transition:
      mode === 'falling' && !reducedMotion ? `transform ${TIMING.STEP_EASE_MS}ms cubic-bezier(0.25, 0.8, 0.35, 1)` : 'none',
  };
  const elapsed = timeLeft === null ? null : Math.min(100, Math.max(0, (1 - timeLeft) * 100));
  const classes = ['sum-block', `sum-block--${mode}`, elapsed !== null && elapsed > 75 && 'sum-block--late'].filter(Boolean).join(' ');

  return (
    <div ref={blockRef} className={classes} style={style}>
      <div ref={contentRef} className="sum-block__content">
        {hasInstruction && (
          <InstructionLine
            instruction={problem.instruction}
            locale={locale}
            className="sum-block__instruction"
            style={instructionStyle}
          />
        )}
        <MathText
          ref={mathRef}
          markup={problem.prompt}
          locale={locale}
          fill={fill}
          className="sum-block__math"
          style={mathStyle}
        />
      </div>
      {elapsed !== null && (
        <svg className="sum-block__timer" aria-hidden="true" focusable="false">
          <rect x="0" y="0" width="100%" height="100%" rx="16" ry="16" pathLength={100} strokeDasharray={`${elapsed.toFixed(2)} 100`} />
        </svg>
      )}
    </div>
  );
}
